import { useGetDevices } from '@vindr/services/actions/devices.action';
import { useGetInstitutions } from '@vindr/services/actions/institution.action';
import { createAttachment } from '@vindr/services/apis/attachment.api';
import { DeviceTypes } from '@vindr/services/constants/common.constant';
import { MODALITIES } from '@vindr/services/constants/study.constant';
import { Col, Form, FormInstance, Row } from 'antd';
import { Dispatch, memo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { handleUpload } from '../context/UploadAction';
import { UploaderAction, UploaderState } from '../context/type';
import { FormItem } from '@vindr/ui';

interface UploadFormProps {
  dispatch: Dispatch<UploaderAction>;
  fileList: UploaderState['fileList'];
  form: FormInstance<any>;
  dicomMode: 'dicom' | 'attachment' | 'pathology';
  params: Parameters<typeof createAttachment>['0'];
  message: any;
}

const UploadForm = ({ dispatch, fileList, form, dicomMode, params, message }: UploadFormProps) => {
  const { data: institutions } = useGetInstitutions({ limit: 100, offset: 0 });
  const { t } = useTranslation('Vindoc');

  const institutionId = Form.useWatch('institution_id', form);
  const { data: devices, isInitialLoading: isFetchedDevices } = useGetDevices(
    {
      limit: 100,
      offset: 0,
      institution_ids: institutionId,
      types: [DeviceTypes.Modality],
      active: true,
    },
    { enabled: !!institutionId }
  );

  const actionError = (error: any) => {
    console.error('UploadForm: ', error);
  };

  const handleSubmit = useCallback(() => {
    form
      .validateFields()
      .then(async (values) => {
        const success = await handleUpload({
          device_code: values?.device_code,
          fileList,
          dispatch,
        });
        if (!success) {
          message.error('Can not upload, please retry or contact administrator for support.');
          dispatch({ type: 'isUploading', payload: false });
        }
      })
      .catch(actionError);
  }, [dispatch, fileList, form]);

  useEffect(() => {
    form.resetFields();
    form.setFieldsValue({
      institution_id: params?.institution_id || institutions?.data?.[0]?.id,
    });
  }, [institutions?.data, params]);

  useEffect(() => {
    const deviceInfo = devices?.data?.find((item) => item?.id === params?.device);

    form.setFieldValue('device', deviceInfo?.id);
    form.setFieldValue('device_code', deviceInfo?.code || undefined);
    form.setFieldValue('modality', deviceInfo?.modality || MODALITIES[13]);
  }, [devices?.data, isFetchedDevices, params?.device]);

  return (
    <Form form={form} layout="vertical" onFinish={handleSubmit}>
      <Row gutter={16}>
        <Col md={16} sm={24} className="w-full">
          <FormItem.InstitutionList
            multiple={false}
            name="institution_id"
            label={t('Institution')}
            required
            allowClear={false}
            disabled={!!params?.institution_id}
            onChange={(val) => {
              form.setFieldValue('device', undefined);
            }}
          />
        </Col>
        <Col md={8} sm={24} className="w-full">
          <FormItem.Device
            institution_ids={institutionId}
            name="device_code"
            keyValue="code"
            allowClear={false}
            onChange={(val) => {
              form.setFieldValue('device', val?.id);
              form.setFieldValue('modality', val?.modality);
            }}
          />
        </Col>
      </Row>
      <Form.Item hidden name="modality">
        <i />
      </Form.Item>
      <Form.Item hidden name="device">
        <i />
      </Form.Item>
      {/* {dicomMode === 'attachment' && (
        <>
          <PatientInformation />
          <OrderInformation />
        </>
      )} */}
    </Form>
  );
};

/* const SectionHeader = ({ title, children }: { title: string; children: React.ReactNode }) => {
  const { t } = useTranslation('Vindoc');

  return (
    <>
      <Divider className="mb-6 mt-0" />
      <div className="flex justify-between">
        <Title level={4}>{t(title)}</Title>
        {children}
      </div>
    </>
  );
};

const PatientInformation = () => {
  const { t } = useTranslation('Vindoc');
  const [enableChangePatient, setEnableChangePatient] = useState(false);

  return (
    <>
      <SectionHeader title={t('Patient Information')}>
        <Checkbox
          name="change_patient"
          className="text-black dark:text-white"
          onChange={(e) => {
            setEnableChangePatient(e.target.checked);
          }}
        >
          {t('Change Value DICOM Fields')}
        </Checkbox>
      </SectionHeader>
      <Row gutter={16}>
        <Col span={8}>
          <FormItem.Input
            name="patient_id"
            label={t('Patient ID')}
            placeholder={t('Enter Patient ID')}
            disabled={!enableChangePatient}
          />
        </Col>
        <Col span={8}>
          <FormItem.Input
            name="patient_name"
            label={t('Patient Name')}
            placeholder={t('Enter Patient Name')}
            disabled={!enableChangePatient}
            required={enableChangePatient}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={8}>
          <FormItem.Birthday name="patient_birthdate" disabled={!enableChangePatient} />
        </Col>
        <Col span={8}>
          <FormItem.Input
            label={t('Age')}
            placeholder={t('Enter Age')}
            disabled={!enableChangePatient}
          />
        </Col>
        <Col span={8}>
          <FormItem.Gender name="patient_sex" disabled={!enableChangePatient} />
        </Col>
      </Row>
    </>
  );
};

const OrderInformation = () => {
  const { t } = useTranslation('Vindoc');
  const [enableChangeOrder, setEnableChangeOrder] = useState(false);

  return (
    <>
      <SectionHeader title={t('Order Information')}>
        <Checkbox
          name="change_order"
          className="text-black dark:text-white"
          onChange={(e) => {
            setEnableChangeOrder(e.target.checked);
          }}
        >
          {t('Change Value DICOM Fields')}
        </Checkbox>
      </SectionHeader>
      <Row gutter={16}>
        <Col flex="auto">
          <FormItem.ProcedureList
            name="procedure_id"
            required={enableChangeOrder}
            disabled={!enableChangeOrder}
          />
        </Col>
        <Col span={8}>
          <FormItem.DatePicker
            name="study_date"
            label={t('Study Date')}
            placeholder={t('Select Date')}
            disabled={!enableChangeOrder}
            required={enableChangeOrder}
            showTime
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <FormItem.Input
            name="accession_no"
            label={t('Order Group')}
            placeholder={t('Enter Order Group')}
            disabled={!enableChangeOrder}
          />
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <FormItem.Input
            name="ref_doctor"
            label={t('Refer Doctor')}
            placeholder={t('Enter Refer Doctor')}
            disabled={!enableChangeOrder}
          />
        </Col>
        <Col span={12}>
          <FormItem.Input
            name="order_doctor"
            label={t('Order Doctor')}
            placeholder={t('Enter Order Doctor')}
            disabled={!enableChangeOrder}
          />
        </Col>
      </Row>
    </>
  );
}; */

export default memo(UploadForm);
